import { useState, useEffect } from 'react';
import { minutesToTime, timeToMinutes, formatTimeInput, normalizeTimeValues } from './timeUtils';

interface UseTimePickerProps {
  value: number | null;
  onChange: (minutes: number | null) => void;
  onExternalBlur?: () => void;
  defaultPeriod?: 'AM' | 'PM';
}

export const useTimePicker = ({ value, onChange, onExternalBlur, defaultPeriod = 'AM' }: UseTimePickerProps) => {
  // Extract initial time and period from the value
  const { time: initialTime, period: initialPeriod } = minutesToTime(value);
  
  // State for the time input, period, and internal tracking
  const [timeInput, setTimeInput] = useState(initialTime);
  const [period, setPeriod] = useState<'AM' | 'PM'>(value === null ? defaultPeriod : initialPeriod);
  const [previousValidValue, setPreviousValidValue] = useState(initialTime);

  // Update internal state when external value changes
  useEffect(() => {
    const { time, period } = minutesToTime(value);
    setTimeInput(time);
    setPeriod(value === null ? defaultPeriod : period);
    setPreviousValidValue(time);
  }, [value, defaultPeriod]);

  // Handle time input changes - now only filters invalid characters
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Only allow numbers and colons
    if (/^[\d:]*$/.test(newValue)) {
      console.log("[TimePicker] Raw input entered:", newValue);
      
      // Store and show exactly what the user types (no formatting while typing)
      setTimeInput(newValue);
    }
  };

  // Handle time input blur - only now validate and format
  const handleTimeBlur = () => {
    // Handle empty field case
    if (!timeInput) {
      setTimeInput('');
      onChange(null);
      setPeriod(defaultPeriod);
      return;
    }
    
    // Validate format - must match HH:MM pattern
    const isValidFormat = /^\d{1,2}:\d{2}$/.test(timeInput);
    
    if (isValidFormat) {
      // Further validate hours and minutes ranges
      const [hoursStr, minutesStr] = timeInput.split(':');
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);
      
      if (hours >= 1 && hours <= 12 && minutes >= 0 && minutes <= 59) {
        // Valid time, format it consistently
        const normalizedTime = `${hours}:${minutes.toString().padStart(2, '0')}`;
        setTimeInput(normalizedTime);
        setPreviousValidValue(normalizedTime);
        
        // Update parent component with minutes since midnight
        const minutesSinceMidnight = timeToMinutes(normalizedTime, period);
        if (minutesSinceMidnight !== null) onChange(minutesSinceMidnight);
        
        // Trigger external onBlur if provided
        if (onExternalBlur) onExternalBlur();
        return;
      }
    }
    
    // If we get here, the format was invalid - revert to previous valid value
    console.log("[TimePicker] Invalid time format, reverting to:", previousValidValue);
    setTimeInput(previousValidValue);
    
    // If the previous value was empty, clear the input and reset to default period
    if (!previousValidValue) {
      onChange(null);
      setPeriod(defaultPeriod);
    }
    
    // Trigger external onBlur if provided
    if (onExternalBlur) onExternalBlur();
  };

  // Handle period (AM/PM) changes
  const handlePeriodChange = (newPeriod: 'AM' | 'PM') => {
    console.log("[TimePicker] Period changed:", newPeriod);
    setPeriod(newPeriod);
    if (timeInput && /^\d{1,2}:\d{2}$/.test(timeInput)) {
      const minutes = timeToMinutes(timeInput, newPeriod);
      if (minutes !== null) {
        onChange(minutes);
        // Update the previous valid value with current period
        setPreviousValidValue(timeInput);
      }
    }
  };

  return {
    timeInput,
    period,
    handleTimeChange,
    handleTimeBlur,
    handlePeriodChange
  };
};
